// === Dashboard card access context ===
//
// Fetches, once per user + company, which dashboard cards the signed-in user may
// see (GET /api/v1/companies/titles/access) and shares it app-wide:
//   • the dashboard and sidebar hide cards the user's job title doesn't include
//   • ProtectedRoute sends a typed-in address for a hidden card to /dashboard
// Company admins have full access. The rules themselves live in card-access.ts.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { CardAccessResponseSchema, type CardAccessResponse } from '@click2gro/schema';
import { useAuth } from './auth';
import { apiFetch } from './api-client';
import { canViewCard } from './card-access';
import { WEB_NAV_GROUPS, type WebNavGroup } from '../config/nav-groups';

interface CardAccessContextValue {
  /** True until the current user + company's access has been fetched. */
  readonly loading: boolean;
  readonly error: string | null;
  /** Company admin (or superadmin viewing as the company) — sees every card. */
  readonly fullAccess: boolean;
  /** Whether the user has a live job title. */
  readonly hasTitle: boolean;
  readonly canView: (cardPath: string) => boolean;
  readonly refresh: () => void;
}

interface LoadedAccess {
  readonly key: string;
  readonly access: CardAccessResponse | null;
  readonly error: string | null;
}

const CardAccessContext = createContext<CardAccessContextValue | null>(null);

const parseApiError = (body: unknown, status: number): string => {
  if (body && typeof body === 'object') {
    const record = body as { error?: unknown };
    if (typeof record.error === 'string' && record.error.length > 0) return record.error;
  }
  return `HTTP ${status}`;
};

export function CardAccessProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const { user, companyId } = useAuth();
  const requestKey = user && companyId ? `${user.id}:${companyId}` : null;
  const latestKeyRef = useRef<string | null>(requestKey);
  const [loaded, setLoaded] = useState<LoadedAccess | null>(null);

  const load = useCallback(async (key: string) => {
    let result: LoadedAccess;
    try {
      const res = await apiFetch('/api/v1/companies/titles/access');
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw new Error(parseApiError(body, res.status));
      const parsed = CardAccessResponseSchema.safeParse(body);
      if (!parsed.success) throw new Error('Unexpected card access data from server');
      result = { key, access: parsed.data, error: null };
    } catch (err: unknown) {
      result = { key, access: null, error: err instanceof Error ? err.message : 'Failed to load card access' };
    }
    // A slow response for a previous user/company must not land on this one.
    if (latestKeyRef.current === key) setLoaded(result);
  }, []);

  useEffect(() => {
    latestKeyRef.current = requestKey;
    if (requestKey !== null) void load(requestKey);
  }, [requestKey, load]);

  // Only trust what was loaded for THIS user + company — never a previous one's.
  const current = loaded !== null && loaded.key === requestKey ? loaded : null;
  const access = current === null ? null : current.access;

  const canView = useCallback(
    (cardPath: string) =>
      access !== null && canViewCard({ fullAccess: access.full_access, visibleCards: access.visible_cards }, cardPath),
    [access],
  );

  const refresh = useCallback(() => {
    if (requestKey !== null) void load(requestKey);
  }, [requestKey, load]);

  const value = useMemo<CardAccessContextValue>(() => ({
    loading: requestKey !== null && current === null,
    error: current === null ? null : current.error,
    fullAccess: access?.full_access === true,
    hasTitle: access !== null && access.title_id !== null,
    canView,
    refresh,
  }), [requestKey, current, access, canView, refresh]);

  return <CardAccessContext.Provider value={value}>{children}</CardAccessContext.Provider>;
}

export function useCardAccess(): CardAccessContextValue {
  const ctx = useContext(CardAccessContext);
  if (!ctx) throw new Error('useCardAccess must be used inside <CardAccessProvider>');
  return ctx;
}

/** Dashboard sections trimmed to the cards the user may see; empty sections dropped. */
export function useVisibleNavGroups(): readonly WebNavGroup[] {
  const { canView } = useCardAccess();
  return useMemo(
    () => WEB_NAV_GROUPS
      .map((group) => ({ heading: group.heading, items: group.items.filter((item) => canView(item.path)) }))
      .filter((group) => group.items.length > 0),
    [canView],
  );
}
